/**
 * What this practitioner still has to set up, driven by what is actually in the database.
 *
 * The onboarding flow has a "Finish later" button and, until now, nothing that brought anyone
 * back: the walkthrough marked itself done the moment it was closed, and the older onboarding
 * banner only fires for people with a form_invitations row. So someone could dismiss both and
 * disappear half-configured (owner, Jun 2026).
 *
 * This sits on the first screen they see and stays until each item is genuinely done. Finished
 * items stay visible and ticked on purpose — "if they see that they have finished things it
 * will be a good thing as well as give them direction on what still needs to be finished."
 */
import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Check, Circle, ArrowRight, Sparkles, Info } from 'lucide-react';

const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

export default function SetupChecklist({ onNavigateTab, hub = 'practitioner' }) {
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(() => {
    axios.get(`${API}/user/setup-progress?hub=${hub}`, auth())
      .then((r) => setData(r.data))
      .catch(() => {});
  }, [hub]);

  useEffect(() => {
    load();
    window.addEventListener('onboarding-status-changed', load);
    return () => window.removeEventListener('onboarding-status-changed', load);
  }, [load]);

  if (!data?.steps?.length || data.dismissed) return null;

  const go = (step) => {
    const tab = step.link.includes('tab=') ? step.link.split('tab=')[1] : null;
    if (tab && onNavigateTab) return onNavigateTab(tab);
    navigate(step.link);
  };

  const skip = (key) => {
    axios.post(`${API}/user/setup-progress/skip`, { key }, auth())
      .then((r) => setData(r.data))
      .catch(() => {});
  };

  const putAway = () => {
    axios.post(`${API}/user/setup-progress/dismiss?hub=${hub}`, {}, auth())
      .then(() => setData((d) => ({ ...d, dismissed: true })))
      .catch(() => {});
  };

  const pct = Math.round((data.done_count / Math.max(data.required_count, 1)) * 100);

  return (
    <div
      data-testid="setup-checklist"
      style={{
        background: data.complete ? 'rgba(110,231,199,0.05)' : 'rgba(255,255,255,0.04)',
        border: `1px solid ${data.complete ? 'rgba(110,231,199,0.25)' : 'rgba(255,255,255,0.1)'}`,
        borderRadius: 14, padding: 18, marginBottom: 24,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <Sparkles size={16} style={{ color: data.complete ? '#6ee7c7' : '#f0c05a' }} />
        <strong style={{ fontSize: 15, color: '#f5f0e8' }}>
          {data.complete ? "Your practice is set up" : 'Finish setting up your practice'}
        </strong>
        <span
          style={{ marginLeft: 'auto', fontSize: 12, color: 'rgba(245,240,232,0.5)' }}
          data-testid="setup-checklist-count"
        >
          {data.done_count} of {data.required_count} done
        </span>
      </div>

      <div style={{
        height: 4, borderRadius: 4, background: 'rgba(255,255,255,0.08)',
        overflow: 'hidden', marginBottom: 14,
      }}>
        <div style={{
          height: '100%', width: `${pct}%`,
          background: data.complete ? '#6ee7c7' : '#f0c05a',
          transition: 'width 400ms ease',
        }} />
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {data.steps.map((step) => (
          <div key={step.key} style={{ display: 'flex', alignItems: 'flex-start' }}>
            <button
              onClick={() => go(step)}
              data-testid={`setup-step-${step.key}`}
              style={{
                display: 'flex', alignItems: 'flex-start', gap: 10, flex: 1,
                textAlign: 'left', padding: '9px 10px', borderRadius: 9, border: 'none',
                background: 'none', cursor: 'pointer',
                transition: 'background-color 160ms ease',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.04)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
            >
            <span style={{ marginTop: 1, flexShrink: 0 }}>
              {step.done
                ? <Check size={15} style={{ color: '#6ee7c7' }} />
                : <Circle size={15} style={{ color: 'rgba(245,240,232,0.3)' }} />}
            </span>
            <span style={{ flex: 1 }}>
              <span style={{
                fontSize: 13,
                color: step.done ? 'rgba(245,240,232,0.5)' : '#f5f0e8',
              }}>
                {step.label}
              </span>
              {step.optional && (
                <span style={{ fontSize: 11, color: 'rgba(245,240,232,0.35)', marginLeft: 6 }}>
                  optional
                </span>
              )}
              {!step.done && (
                <span style={{
                  display: 'block', fontSize: 11.5, color: 'rgba(245,240,232,0.45)',
                  marginTop: 2, lineHeight: 1.5,
                }}>
                  {step.why}
                </span>
              )}
              {!step.done && step.blocked_reason && (
                <span style={{
                  display: 'block', fontSize: 11.5, color: '#f0c05a', marginTop: 3,
                }} data-testid={`setup-step-${step.key}-blocked`}>
                  {step.blocked_reason}
                </span>
              )}
            </span>
            {!step.done && (
              <ArrowRight size={14} style={{ color: 'rgba(245,240,232,0.3)', marginTop: 2 }} />
            )}
            </button>
            {step.optional && !step.done && (
              <button
                onClick={() => skip(step.key)}
                data-testid={`setup-step-${step.key}-skip`}
                title="Take this off my list"
                style={{
                  border: 'none', background: 'none', cursor: 'pointer', padding: '10px 8px',
                  fontSize: 11, color: 'rgba(245,240,232,0.35)', whiteSpace: 'nowrap',
                  transition: 'color 160ms ease',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.color = 'rgba(245,240,232,0.7)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(245,240,232,0.35)'; }}
              >
                Not for me
              </button>
            )}
          </div>
        ))}
      </div>

      {data.complete ? (
        <div style={{
          display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10,
          margin: '12px 0 0', padding: '10px 11px', borderRadius: 8,
          background: 'rgba(110,231,199,0.07)',
        }}>
          <p style={{ flex: 1, minWidth: 220, margin: 0, fontSize: 12, lineHeight: 1.55,
                      color: 'rgba(245,240,232,0.7)' }}>
            Everything required is done. Put this away and it will not come back — the
            walkthroughs stay in your Quick Actions.
          </p>
          <button
            onClick={putAway}
            data-testid="setup-checklist-dismiss"
            style={{
              border: '1px solid rgba(110,231,199,0.35)', background: 'none', cursor: 'pointer',
              color: '#6ee7c7', fontSize: 12, fontWeight: 600, padding: '7px 14px',
              borderRadius: 8,
            }}
          >
            Hide this card
          </button>
        </div>
      ) : (
        <p style={{
          display: 'flex', gap: 7, fontSize: 11.5, lineHeight: 1.55, margin: '12px 0 0',
          padding: '9px 11px', borderRadius: 8,
          color: 'rgba(245,240,232,0.6)', background: 'rgba(255,255,255,0.03)',
        }} data-testid="setup-checklist-pricing-note">
          <Info size={13} style={{ flexShrink: 0, marginTop: 1, color: 'rgba(245,240,232,0.45)' }} />
          Setting all of this up is free until launch day. After launch, taking bookings in the app
          is a subscription — your external booking link and your storefront stay free.
        </p>
      )}
    </div>
  );
}
